import { Country } from 'constants/country'
import { Currency } from 'constants/misc'
import { SubscriptionPeriod } from 'models/user/userWorkspaceDTOs'

/** Currencies a subscription plan can be sold in — one per currency Chargebee mints an item price for. */
export type PlanCurrency = Currency.EUR | Currency.CHF | Currency.GBP

/** Translation key of the period noun rendered after the Pro amount. The surfaces own the separator. */
export const PRO_PERIOD_KEY = 'pricing_page:plans.pro.period'

/** Label shown in place of an amount when no price is published for the resolved currency. */
export const PRO_PRICE_UNAVAILABLE_KEY = 'pricing_page:plans.pro.price_unavailable'

/** Language every surface falls back to when the display language is not a tag Intl accepts. */
const FALLBACK_LANGUAGE = 'en'

/**
 * Currency the customer's checkout will bill in.
 *
 * Chargebee bills the currency in the item price id payments builds, and that comes from the
 * customer's own country: `ChargebeeClient.itemPriceId` is `"$planId-${country.currency()}-$period"`,
 * where `Country.currency()` (payments-api) maps CH to CHF, GB to GBP and every other country to
 * EUR. The site the call is made against is chosen separately (`mapWorkspaceCountryToInstanceCountry`)
 * and does not decide the currency.
 *
 * The country platform sends with a new checkout is the buying user's own country, not the
 * workspace creator's (`WorkspaceSubscriptionService`) — the same field read here. Plan *changes*
 * resolve from payments' stored workspace country instead, which platform writes from the creator
 * (`WorkspaceCreator`), so the two can disagree on a workspace someone else created.
 */
export const planCurrencyForCountry = (country?: string | null): PlanCurrency => {
  switch (country?.trim().toUpperCase()) {
    case Country.CH: return Currency.CHF
    case Country.GB: return Currency.GBP
    default: return Currency.EUR
  }
}

/** Whether a country is one the app knows, so an absent or unrecognised one can be reported. */
export const isKnownPlanCountry = (country?: string | null): boolean =>
  Object.values(Country).includes(country?.trim().toUpperCase() as Country)

/**
 * Price of one Pro seat per month, in the currency's main unit. The yearly entry is the discounted
 * monthly rate a yearly subscription bills twelve of, up front. Pro is sold at the same figure in
 * the currencies it is published in, so only the currency changes between them.
 *
 * Mirrors the Chargebee item prices (`Pro-Per-Seat-<currency>-<period>` on the site for that
 * currency) by hand, because no endpoint serves the amount. A currency whose figure has not been
 * published stays null and renders the "shown at checkout" label — never another currency's amount.
 */
export const PRO_SEAT_MONTHLY_PRICE: Record<PlanCurrency, Record<SubscriptionPeriod, number | null>> = {
  [Currency.EUR]: {
    [SubscriptionPeriod.MONTHLY]: 19,
    [SubscriptionPeriod.YEARLY]: 16,
  },
  [Currency.CHF]: {
    [SubscriptionPeriod.MONTHLY]: 19,
    [SubscriptionPeriod.YEARLY]: 16,
  },
  [Currency.GBP]: {
    [SubscriptionPeriod.MONTHLY]: null,
    [SubscriptionPeriod.YEARLY]: null,
  },
}

/**
 * The published seat price, or null when there is none. Everything a surface renders as money goes
 * through here: a table cell that is absent or not a usable number has to reach the fallback label,
 * not arithmetic that renders "CHF NaN" on a checkout confirmation.
 */
export const publishedSeatPrice = (currency: PlanCurrency, period: SubscriptionPeriod): number | null => {
  const amount = PRO_SEAT_MONTHLY_PRICE[currency]?.[period]

  return typeof amount === 'number' && Number.isFinite(amount) && amount > 0 ? amount : null
}

/**
 * Discount the yearly period advertises, derived from the two published figures so the percentage
 * cannot contradict the prices beside it. Null when either figure is unpublished — a currency we
 * quote no price in cannot advertise a saving on it either.
 */
export const annualDiscountPercent = (currency: PlanCurrency): number | null => {
  const monthly = publishedSeatPrice(currency, SubscriptionPeriod.MONTHLY)
  const yearly = publishedSeatPrice(currency, SubscriptionPeriod.YEARLY)
  if (monthly === null || yearly === null || yearly >= monthly) return null

  return Math.round(((monthly - yearly) / monthly) * 100)
}

/**
 * Formats a plan amount in the currency the customer will be charged in. Plan prices are whole
 * numbers and render without decimals — the generic `formatPrice` always shows two, which reads
 * wrong beside a crossed-out figure on a plan card.
 *
 * The display language is tried before its bare primary subtag and then English, because
 * `i18n.language` is whatever the `?lang` query put there: i18next keeps the raw tag when only its
 * primary subtag matches a supported language, and caches it in localStorage. A malformed tag makes
 * Intl throw, and this runs inside render — unguarded, one crafted link would take the plan
 * surfaces down for that browser until storage is cleared.
 */
export const formatPlanPrice = (amount: number, currency: PlanCurrency, language?: string | null): string => {
  const fractionDigits = Number.isInteger(amount) ? 0 : 2
  const options: Intl.NumberFormatOptions = {
    style: 'currency',
    currency,
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }

  for (const candidate of [language, language?.split('-')[0], FALLBACK_LANGUAGE]) {
    if (!candidate) continue
    try {
      return new Intl.NumberFormat(candidate, options).format(amount)
    } catch {
      continue
    }
  }

  return `${currency} ${amount}`
}
